import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getTrail, updateTrail } from '../../services/cloudtrailApi';

const dataResourceTypes = [
  'AWS::S3::Object',
  'AWS::Lambda::Function',
  'AWS::DynamoDB::Table',
];

const networkEventSources = [
  'aco-automation.amazonaws.com',
  'appconfig.amazonaws.com',
  'application-signals.amazonaws.com',
  'appmesh.amazonaws.com',
  'athena.amazonaws.com',
  'b2bi.amazonaws.com',
  'backup-gateway.amazonaws.com',
  'bcm-data-exports.amazonaws.com',
  'bcm-pricing-calculator.amazonaws.com',
  'bedrock.amazonaws.com',
  'billing.amazonaws.com',
  'cassandra.amazonaws.com',
  'ce.amazonaws.com',
  'cloudcontrolapi.amazonaws.com',
  'cloudformation.amazonaws.com',
  'cloudhsm.amazonaws.com',
  'cloudtrail.amazonaws.com',
  'codedeploy.amazonaws.com',
  'comprehend.amazonaws.com',
  'comprehendmedical.amazonaws.com',
  'config.amazonaws.com',
  'ds.amazonaws.com',
  'dynamodb.amazonaws.com',
  'ec2.amazonaws.com',
  'ecs.amazonaws.com',
  'elasticfilesystem.amazonaws.com',
  'elasticloadbalancing.amazonaws.com',
  'events.amazonaws.com',
  'firehose.amazonaws.com',
  'frauddetector.amazonaws.com',
  'freetier.amazonaws.com',
  'fsx.amazonaws.com',
  'glue.amazonaws.com',
  'healthlake.amazonaws.com',
  'invoicing.amazonaws.com',
  'iot.amazonaws.com',
  'iotfleetwise.amazonaws.com',
  'iotsecuredtunneling.amazonaws.com',
  'kms.amazonaws.com',
  'lakeformation.amazonaws.com',
  'lambda.amazonaws.com',
  'license-manager.amazonaws.com',
  'lookoutequipment.amazonaws.com',
  'lookoutvision.amazonaws.com',
  'monitoring.amazonaws.com',
  'personalize.amazonaws.com',
  'qbusiness.amazonaws.com',
  'rds.amazonaws.com',
  'rekognition.amazonaws.com',
  'rolesanywhere.amazonaws.com',
  's3.amazonaws.com',
  'sagemaker.amazonaws.com',
  'scheduler.amazonaws.com',
  'secretsmanager.amazonaws.com',
  'servicediscovery.amazonaws.com',
  'sns.amazonaws.com',
  'sqs.amazonaws.com',
  'ssm-contacts.amazonaws.com',
  'ssm.amazonaws.com',
  'storagegateway.amazonaws.com',
  'swf.amazonaws.com',
  'textract.amazonaws.com',
  'transcribe.amazonaws.com',
  'transcribestreaming.amazonaws.com',
  'transform.amazonaws.com',
  'translate.amazonaws.com',
  'user-subscriptions.amazonaws.com',
  'verifiedpermissions.amazonaws.com',
  'voiceid.amazonaws.com',
  'workmail.amazonaws.com',
  'workmailmessageflow.amazonaws.com',
];

const EditTrail = () => {
  const { trailId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [trail, setTrail] = useState(null);

  // Trail Settings
  const [isMultiRegion, setIsMultiRegion] = useState(true);
  const [s3Prefix, setS3Prefix] = useState('');

  // Event Configuration
  const [mgmtEnabled, setMgmtEnabled] = useState(true);
  const [mgmtReadWriteType, setMgmtReadWriteType] = useState('All');
  const [excludeKMS, setExcludeKMS] = useState(false);
  const [excludeRDSData, setExcludeRDSData] = useState(false);
  const [dataEvents, setDataEvents] = useState([]);
  const [insightApiCall, setInsightApiCall] = useState(false);
  const [insightApiError, setInsightApiError] = useState(false);
  const [networkEvents, setNetworkEvents] = useState([]);

  const fetchTrail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTrail(trailId);
      const data = response.data;
      setTrail(data);

      // Populate form state
      setIsMultiRegion(data.isMultiRegion);
      setS3Prefix(data.s3Prefix || '');

      // Parse event config
      const ec = data.eventConfig;
      if (ec) {
        if (ec.managementEvents) {
          setMgmtEnabled(ec.managementEvents.enabled);
          setMgmtReadWriteType(ec.managementEvents.readWriteType || 'All');
          setExcludeKMS(ec.managementEvents.excludeKMS || false);
          setExcludeRDSData(ec.managementEvents.excludeRDSData || false);
        } else {
          setMgmtEnabled(false);
        }

        if (ec.dataEvents && ec.dataEvents.length > 0) {
          setDataEvents(
            ec.dataEvents.map((de) => ({
              resourceType: de.resourceType,
              readWriteType: de.readWriteType || 'All',
              resourceArns: (de.resourceArns || []).join(', '),
            }))
          );
        }

        if (ec.insightSelectors && ec.insightSelectors.length > 0) {
          ec.insightSelectors.forEach((is) => {
            if (is.insightType === 'ApiCallRateInsight') setInsightApiCall(true);
            if (is.insightType === 'ApiErrorRateInsight') setInsightApiError(true);
          });
        }

        if (ec.networkActivityEvents && ec.networkActivityEvents.length > 0) {
          const legacyMap = {
            'AWS::EC2::VPC': 'ec2.amazonaws.com',
            'AWS::EC2::Subnet': 'ec2.amazonaws.com',
            'AWS::EC2::NetworkInterface': 'ec2.amazonaws.com',
          };
          setNetworkEvents(
            ec.networkActivityEvents.map((ne) => ({
              eventSource: ne.eventSource || legacyMap[ne.resourceType] || '',
            }))
          );
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [trailId]);

  useEffect(() => {
    fetchTrail();
  }, [fetchTrail]);

  const addDataEvent = () => {
    setDataEvents([...dataEvents, { resourceType: 'AWS::S3::Object', readWriteType: 'All', resourceArns: '' }]);
  };

  const removeDataEvent = (index) => {
    setDataEvents(dataEvents.filter((_, i) => i !== index));
  };

  const updateDataEvent = (index, field, value) => {
    const updated = [...dataEvents];
    updated[index] = { ...updated[index], [field]: value };
    setDataEvents(updated);
  };

  const addNetworkEvent = () => {
    setNetworkEvents([...networkEvents, { eventSource: '' }]);
  };

  const removeNetworkEvent = (index) => {
    setNetworkEvents(networkEvents.filter((_, i) => i !== index));
  };

  const updateNetworkEvent = (index, field, value) => {
    const updated = [...networkEvents];
    updated[index] = { ...updated[index], [field]: value };
    setNetworkEvents(updated);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const eventConfig = {};

    if (mgmtEnabled) {
      eventConfig.managementEvents = {
        enabled: true,
        readWriteType: mgmtReadWriteType,
        excludeKMS,
        excludeRDSData,
      };
    }

    if (dataEvents.length > 0) {
      eventConfig.dataEvents = dataEvents.map((de) => ({
        resourceType: de.resourceType,
        readWriteType: de.readWriteType,
        resourceArns: de.resourceArns
          ? de.resourceArns.split(',').map((s) => s.trim()).filter(Boolean).map((arn) =>
              de.resourceType === 'AWS::S3::Object' && !arn.endsWith('/') ? arn + '/' : arn
            )
          : [],
      }));
    }

    const insightSelectors = [];
    if (insightApiCall) insightSelectors.push({ insightType: 'ApiCallRateInsight' });
    if (insightApiError) insightSelectors.push({ insightType: 'ApiErrorRateInsight' });
    if (insightSelectors.length > 0) {
      eventConfig.insightSelectors = insightSelectors;
    }

    if (networkEvents.length > 0) {
      eventConfig.networkActivityEvents = networkEvents.map((ne) => ({
        eventSource: ne.eventSource,
      }));
    }

    try {
      await updateTrail(trailId, {
        isMultiRegion,
        s3Prefix,
        eventConfig,
      });

      toast.success('Trail update started — tracking progress');
      navigate(`/trails/${trailId}`);
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to update trail: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  if (error && !trail) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/trails/${trailId}`)} sx={{ mb: 2, textTransform: 'none' }}>
          Back to Trail
        </Button>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const sectionSx = {
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    color: '#253E66',
    mb: 2,
    fontSize: '1rem',
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/trails/${trailId}`)}
          sx={{
            textTransform: 'none',
            color: '#666',
            fontFamily: 'Metropolis, sans-serif',
            mb: 2,
          }}
        >
          Back to Trail
        </Button>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
          }}
        >
          Edit Trail
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Metropolis, sans-serif', mt: 0.5 }}>
          {trail.name}
        </Typography>
      </Box>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Trail Settings */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #E5E5E5', borderRadius: '8px' }}>
        <Typography sx={sectionSx}>Trail Settings</Typography>

        <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif', mb: 0.5 }}>
          Trail Name
        </Typography>
        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', mb: 2 }}>
          {trail.name}
        </Typography>

        <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Metropolis, sans-serif', mb: 0.5 }}>
          Region
        </Typography>
        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', mb: 2 }}>
          {trail.region || 'us-east-1'}
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={isMultiRegion}
              onChange={(e) => setIsMultiRegion(e.target.checked)}
              color="primary"
            />
          }
          label="Multi-region trail"
        />
        <Typography variant="body2" sx={{ color: '#666', ml: 6, mt: -0.5, mb: 3 }}>
          Log events from all AWS regions
        </Typography>

        <TextField
          label="S3 Key Prefix (optional)"
          value={s3Prefix}
          onChange={(e) => setS3Prefix(e.target.value)}
          fullWidth
          helperText="Optional prefix for CloudTrail log files in the bucket"
        />
      </Paper>
      </motion.div>

      {/* Event Configuration */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #E5E5E5', borderRadius: '8px' }}>
        <Typography sx={sectionSx}>Event Configuration</Typography>

        {/* Management Events */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
          <FormControlLabel
            control={
              <Switch checked={mgmtEnabled} onChange={(e) => setMgmtEnabled(e.target.checked)} />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Management Events</Typography>}
          />
          {mgmtEnabled && (
            <Box sx={{ ml: 4, mt: 1 }}>
              <FormControl size="small" sx={{ minWidth: 200, mb: 1 }}>
                <InputLabel>Read/Write Type</InputLabel>
                <Select
                  value={mgmtReadWriteType}
                  onChange={(e) => setMgmtReadWriteType(e.target.value)}
                  label="Read/Write Type"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="ReadOnly">Read Only</MenuItem>
                  <MenuItem value="WriteOnly">Write Only</MenuItem>
                </Select>
              </FormControl>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={excludeKMS} onChange={(e) => setExcludeKMS(e.target.checked)} size="small" />}
                  label="Exclude AWS KMS events"
                />
                <FormControlLabel
                  control={<Checkbox checked={excludeRDSData} onChange={(e) => setExcludeRDSData(e.target.checked)} size="small" />}
                  label="Exclude Amazon RDS Data API events"
                />
              </FormGroup>
            </Box>
          )}
        </Paper>

        {/* Data Events */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>Data Events</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addDataEvent} sx={{ textTransform: 'none' }}>
              Add
            </Button>
          </Box>
          {dataEvents.length === 0 && (
            <Typography variant="body2" sx={{ color: '#999' }}>No data event selectors configured</Typography>
          )}
          {dataEvents.map((de, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap' }}>
              <Autocomplete
                options={dataResourceTypes}
                value={de.resourceType}
                onChange={(_, newValue) => updateDataEvent(idx, 'resourceType', newValue || 'AWS::S3::Object')}
                size="small"
                sx={{ minWidth: 260 }}
                renderInput={(params) => (
                  <TextField {...params} label="Resource Type" placeholder="Search resource types..." />
                )}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Read/Write</InputLabel>
                <Select
                  value={de.readWriteType}
                  onChange={(e) => updateDataEvent(idx, 'readWriteType', e.target.value)}
                  label="Read/Write"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="ReadOnly">Read Only</MenuItem>
                  <MenuItem value="WriteOnly">Write Only</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Resource ARNs (comma-separated, optional)"
                value={de.resourceArns}
                onChange={(e) => updateDataEvent(idx, 'resourceArns', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <IconButton size="small" onClick={() => removeDataEvent(idx)} sx={{ color: '#E74C3C', mt: 0.5 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Paper>

        {/* Insights */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Insight Events</Typography>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={insightApiCall} onChange={(e) => setInsightApiCall(e.target.checked)} size="small" />}
              label="API call rate insight"
            />
            <FormControlLabel
              control={<Checkbox checked={insightApiError} onChange={(e) => setInsightApiError(e.target.checked)} size="small" />}
              label="API error rate insight"
            />
          </FormGroup>
        </Paper>

        {/* Network Activity */}
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>Network Activity Events</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  const existing = new Set(networkEvents.map((ne) => ne.eventSource));
                  const toAdd = networkEventSources.filter((src) => !existing.has(src));
                  if (toAdd.length > 0) {
                    setNetworkEvents([...networkEvents, ...toAdd.map((src) => ({ eventSource: src }))]);
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Select All
              </Button>
              <Button size="small" startIcon={<AddIcon />} onClick={addNetworkEvent} sx={{ textTransform: 'none' }}>
                Add
              </Button>
            </Box>
          </Box>
          {networkEvents.length === 0 && (
            <Typography variant="body2" sx={{ color: '#999' }}>No network activity event selectors configured</Typography>
          )}
          {networkEvents.map((ne, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
              <Autocomplete
                freeSolo
                options={networkEventSources}
                value={ne.eventSource || ''}
                onChange={(_, newValue) => updateNetworkEvent(idx, 'eventSource', newValue || '')}
                onInputChange={(_, newInput, reason) => {
                  if (reason === 'input') updateNetworkEvent(idx, 'eventSource', newInput);
                }}
                size="small"
                sx={{ minWidth: 300, flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="Event Source" placeholder="e.g. ec2.amazonaws.com" />
                )}
              />
              <IconButton size="small" onClick={() => removeNetworkEvent(idx)} sx={{ color: '#E74C3C' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Paper>
      </Paper>
      </motion.div>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            backgroundColor: '#4698D3',
            textTransform: 'none',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            borderRadius: '6px',
            px: 4,
            '&:hover': { backgroundColor: '#3A7BB0' },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Box>
    </motion.div>
  );
};

export default EditTrail;
